import { FilterList } from "@mui/icons-material";
import { Box, Divider, Grid, Stack, Typography, Paper } from "@mui/material";
import Spinner from "components/Spinner";
import { _getLocation } from "middlewares/LocationApi/location";
import { LocationModel } from "object-models/location-model";
import MultiSelect from "pages/TargetForm/MultiSelect";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import BaselineYearAndTotal from "pages/PortfolioView/BaselineEmission/BaselineYearAndTotal";
import EmissionChartPanel from "pages/PortfolioView/BaselineEmission/EmissionChartPanel";
import EmissionByScopeCategoryBar from "pages/PortfolioView/BaselineEmission/EmissionByScopeCategoryBar";
import EmissionByScopePieChat from "pages/PortfolioView/BaselineEmission/EmissionByScopePieChat";
import TotalEmissionsByScope from "pages/PortfolioView/BaselineEmission/TotalEmissionsByScope";
import { Constants } from "constants/constant";

const EMPTY_RAW_ROWS: Array<Partial<LocationModel>> = [];

/**
 * Component for displaying Baseline Target Emission analysis.
 */
export default function BaselineTargetEmission() {
  const { LocationId } = useParams();
  const scopesCoveredOptions = [
    { id: 0, label: "Scope 1", value: "Scope 1" },
    { id: 1, label: "Scope 2", value: "Scope 2" },
    { id: 2, label: "Scope 3", value: "Scope 3" },
  ];

  const businessUnitOptions = [
    { id: 0, label: "Unit 1", value: "Unit 1" },
    { id: 1, label: "Unit 2", value: "Unit 2" },
    { id: 2, label: "Unit 3", value: "Unit 3" },
    { id: 3, label: "Unit 4", value: "Unit 4" },
    { id: 4, label: "Unit 5", value: "Unit 5" },
  ];
  const [selectedScope, setSelectedScope] = useState(
    scopesCoveredOptions.map((a) => a.id)
  );
  const [selectedBU, setSelectedBU] = useState(
    businessUnitOptions.map((b) => b.id)
  );

  /**
   * Handles scope change.
   * @param selectedOptions - Array of selected scope options.
   */
  const handleScopeChange = (selectedOptions: number[]) => {
    setSelectedScope(selectedOptions);
  };

  /**
   * Handles business unit change.
   * @param selectedOptions - Array of selected business unit options.
   */
  const handleBusinessUnitChange = (selectedOptions: number[]) => {
    setSelectedBU(selectedOptions);
  };
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([] as unknown as LocationModel);
  const [emissionValues, setEmissionValues] = useState<{ [key: string]: number }>({
    "Scope 1": 0,
    "Scope 2": 0,
    "Scope 3": 0,
  });
  const [scopeSum, setScopeSum] = useState(0);

  /**
   * Fetches location details using the Location API.
   */
  const fetchLocationDetails = async () => {
    try {
      setLoading(true);
      const res = await _getLocation(Number(LocationId));
      if (res) {
        setData(res);
      }
    } catch (err) {
      console.error("Error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const newEmissionValues: { [key: string]: number } = {
      "Scope 1": 0,
      "Scope 2": 0,
      "Scope 3": 0,
    };

    selectedScope.forEach((option) => {
      switch (option) {
        case 0:
          newEmissionValues["Scope 1"] = Number(data.scope1GHGEmission) || 0;
          break;
        case 1:
          newEmissionValues["Scope 2"] = Number(data.scope2GHGEmission) || 0;
          break;
        case 2:
          newEmissionValues["Scope 3"] = Number(data.scope3GHGEmission) || 0;
          break;
      }
    });

    setEmissionValues(newEmissionValues);
    setScopeSum(
      newEmissionValues["Scope 1"] +
        newEmissionValues["Scope 2"] +
        newEmissionValues["Scope 3"]
    );
  }, [selectedScope, data]);
  useEffect(() => {
    fetchLocationDetails();
  }, []);

  return (
    <Box>
      <Spinner size={80} data-testid="spinner" open={loading} />
      <Grid container spacing={2} sx={{ width: "100%", maxWidth: "100%" }}>
        <Grid item xs={12}>
          <Typography variant="body1" sx={{ fontWeight: "bold",fontSize: "0.89rem" }}>
            Baseline and Target Emission Analysis
          </Typography>
        </Grid>
      </Grid>
      <Divider sx={{ my: 1.3 }} />

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          mb: 2.5,
          flexWrap: "wrap",
        }}
      >
        <FilterList sx={{ color: "#E57A1F", fontSize: 20 }} />
        <Typography sx={{ color: "#475569", fontWeight: 500, fontSize: "0.9rem" }}>
          Filters:
        </Typography>
        <Box sx={{ minWidth: { xs: "100%", md: 320 }, flex: { md: "0 0 320px" } }}>
          <MultiSelect
            dataTestId={"scopeSelect"}
            options={scopesCoveredOptions}
            onSelectionChange={handleScopeChange}
            selectedOptions={selectedScope}
          />
        </Box>
      </Box>

      <Grid container spacing={2} sx={{ width: "100%", maxWidth: "100%" }}>
        <Grid item xs={12} md={4.8} sx={{ width: "100%", maxWidth: "100%" }}>
          <BaselineYearAndTotal
            totalEmissions={scopeSum}
            baseYear={data.baseYear}
            displayContext="location"
          />
        </Grid>
        <Grid item xs={4} hidden={true}>
          <Stack direction={"row"}>
            <Typography variant="body1" sx={{ m: 1 }}>
              Business Unit{" "}
            </Typography>
            <MultiSelect
              options={businessUnitOptions}
              onSelectionChange={handleBusinessUnitChange}
              selectedOptions={selectedBU}
            />
          </Stack>
        </Grid>

        <Grid item xs={12} md={7.2} sx={{ width: "100%", maxWidth: "100%" }}>
          <TotalEmissionsByScope
            emissionValues={emissionValues}
            selectedScope={selectedScope}
            displayContext="location"
          />
        </Grid>
      </Grid>

      <EmissionChartPanel>
        <Grid container sx={{ width: "100%", maxWidth: "100%" }}>
          <EmissionByScopePieChat
            data={data}
            selectedScope={selectedScope}
            assessmentView="scope"
            rawRows={EMPTY_RAW_ROWS}
            displayContext="location"
          />
          <EmissionByScopeCategoryBar
            data={data}
            assessmentView="scope"
            rawRows={EMPTY_RAW_ROWS}
            selectedScope={selectedScope}
            displayContext="location"
          />
        </Grid>
      </EmissionChartPanel>
    </Box>
  );
}
